import { IPQuestion, IDryRunAnswerData } from "modules/organization/models/interface";
import { EquationParser, ILogicalExpression } from "../../utils/equationParser";
import { OPEN_ENDED_QUESTION_TYPE_IDS } from "../../utils/equationValidation";

// Client-side port of mn-api-helpers/src/modules/transform/calculateEquation.ts's
// CalculateEquation class, so the Equation Editor's Dry Run feature can evaluate an equation
// (including one with unsaved edits) against a real record's answers entirely in the browser —
// no server round-trip to run the calculation. Operator semantics below are copied verbatim
// from that file; keep them in sync if the backend engine's behavior ever changes.
const ANSWER_NOT_EXISTS_SUFFIX = "_answer_not_exists";
const ANSWER_EXISTS_SUFFIX = "_answer_exists";

interface IQuestionResponseVariable {
  questionVariable: string;
  response: string[];
}

export interface IAllEquationSet {
  name: string;
  equation: string;
  result: 0 | 1 | null;
  display_order?: number | null;
}

// One node of the Dry Run step-by-step breakdown tree — built by getResultWithSteps(), walking
// the same [operatorName, argsArray] pair structure equationErrorRanges.ts's `walk` already
// consumes, rather than the mutate-in-place array getResult() below uses (that shape is much
// harder to instrument cleanly for a display tree).
export interface IDryRunStepNode {
  label: string;
  // "literal" is a non-variable argument (a COMPARE/EQUAL comparison value, a COMPARE operator
  // symbol like "<", a NUMBER/DATE type tag) — listed for full argument transparency, but with
  // no meaningful truthy/falsy result of its own, so the UI skips its result badge.
  kind: "operator" | "leaf" | "import" | "literal";
  result: number;
  detail?: string;
  // The record's actual typed answer text — only populated for an open-ended question's
  // leaf (QHASANSWER/QHASNOTANSWER's question variable) or its "_answer_exists" response
  // variable (EQUAL/NOTEQUAL/COMPARE), since those are the only places a real free-text/date/
  // number/currency value exists to show; every other variable is a categorical selection
  // already fully described by its own label.
  answerValue?: string;
  children?: IDryRunStepNode[];
  importedRankName?: string;
  importedEquationText?: string;
  importedTree?: IDryRunStepNode | null;
}

class DryRunCalculateEquation extends EquationParser {
  private questions: IPQuestion[];
  private answers: IDryRunAnswerData[];
  private questionResponseVariables: IQuestionResponseVariable[];
  private allEquations: IAllEquationSet[];
  private selfDisplayOrder: number | null;

  constructor(
    equation: string,
    questionData: IPQuestion[],
    answerData: IDryRunAnswerData[],
    allEquationSet: IAllEquationSet[],
    selfDisplayOrder?: number | null,
  ) {
    super(equation);
    this.questions = questionData;
    this.answers = answerData;
    this.allEquations = allEquationSet;
    this.selfDisplayOrder = selfDisplayOrder ?? null;

    this.questionResponseVariables = questionData.map((eachQuestion) => {
      return {
        questionVariable: eachQuestion.question_variable,
        response: eachQuestion.responses.map((eachRes) => eachRes.response_variable),
      };
    });
  }

  private isQuestionVariablePresentInQuestion(questionVar: string) {
    return !!this.questionResponseVariables.find((each) => each.questionVariable === questionVar);
  }

  private isResponseVariablePresentInQuestion(responseVar: string) {
    return !!this.questionResponseVariables.find((each) =>
      !!each.response.find((eachRes) => eachRes === responseVar),
    );
  }

  private isAnswerNotExistsResponseVariable(responseVar: string) {
    if (!responseVar.endsWith(ANSWER_NOT_EXISTS_SUFFIX)) {
      return false;
    }
    const owningQuestion = this.questions.find((question) =>
      question.responses.some((response) => response.response_variable === responseVar),
    );
    return !!owningQuestion && OPEN_ENDED_QUESTION_TYPE_IDS.includes(owningQuestion.question_type_id);
  }

  // QAND/QOR run AND/OR over EVERY one of the question's own response variables — including its
  // synthesized "_answer_not_exists" half for an open-ended question, which
  // isAnswerNotExistsResponseVariable/responseVariableHasInAnswer above always reject. So
  // QAND/QOR on an open-ended question (types 1/2/3/7) always throws by construction — rejected
  // explicitly here, mirroring mn-api-helpers's calculateEquation.ts.
  private isOpenEndedQuestionVariable(questionVar: string) {
    const question = this.questions.find((eachQuestion) => eachQuestion.question_variable === questionVar);
    return !!question && OPEN_ENDED_QUESTION_TYPE_IDS.includes(question.question_type_id);
  }

  private questionHasRealAnswer(questionVar: string, questionResponseVariable: IQuestionResponseVariable) {
    const findAnswer = this.answers.find((each) => each.questionVariable === questionVar);
    if (!findAnswer) {
      return false;
    }
    return findAnswer.ansResponse.some((eachRes) =>
      questionResponseVariable.response.some((responseVar) => !!eachRes[responseVar]),
    );
  }

  private responseVariableHasInAnswer(responseVar: string) {
    if (!responseVar) {
      throw new Error("Response variable not present");
    }

    if (!this.isResponseVariablePresentInQuestion(responseVar)) {
      throw new Error(`Response variable not found: ${responseVar}`);
    }

    if (this.isAnswerNotExistsResponseVariable(responseVar)) {
      throw new Error(
        `${responseVar} can't be used here — use QHASNOTANSWER on the question variable instead.`,
      );
    }

    const foundAnswer = this.answers.find((eachAns) =>
      !!eachAns.ansResponse.find((each) => !!each[responseVar]),
    );

    return !!foundAnswer;
  }

  private operatorAND(variables: (string | number)[]) {
    const allVar = [...variables];
    if (!allVar.length) {
      return 0;
    }
    allVar.forEach((eachVar, index) => {
      if (typeof eachVar === "string") {
        allVar[index] = this.responseVariableHasInAnswer(eachVar) ? 1 : 0;
      }
    });
    return allVar.length === allVar.filter((each) => each === 1).length ? 1 : 0;
  }

  private operatorOR(variables: (string | number)[]) {
    const allVar = [...variables];
    if (!allVar.length) {
      return 0;
    }
    allVar.forEach((eachVar, index) => {
      if (typeof eachVar === "string") {
        allVar[index] = this.responseVariableHasInAnswer(eachVar) ? 1 : 0;
      }
    });
    return allVar.find((each) => each === 1) ? 1 : 0;
  }

  private operatorQAND(questionVariables: string[]) {
    if (!questionVariables.length || questionVariables.length > 1) {
      throw new Error("QAND only take one question variable");
    }
    if (!this.isQuestionVariablePresentInQuestion(questionVariables[0])) {
      throw new Error(`Question variable not found: ${questionVariables[0]}`);
    }
    if (this.isOpenEndedQuestionVariable(questionVariables[0])) {
      throw new Error(
        `QAND(${questionVariables[0]}) can't be used here — this question doesn't support QAND. Use QHASANSWER or QHASNOTANSWER instead.`,
      );
    }
    const findQuestion = this.questionResponseVariables.find(
      (each) => each.questionVariable === questionVariables[0],
    );
    return findQuestion ? this.operatorAND(findQuestion.response) : 0;
  }

  private operatorQOR(questionVariables: string[]) {
    if (!questionVariables.length || questionVariables.length > 1) {
      throw new Error("QOR only take one question variable");
    }
    if (!this.isQuestionVariablePresentInQuestion(questionVariables[0])) {
      throw new Error(`Question variable not found: ${questionVariables[0]}`);
    }
    if (this.isOpenEndedQuestionVariable(questionVariables[0])) {
      throw new Error(
        `QOR(${questionVariables[0]}) can't be used here — this question doesn't support QOR. Use QHASANSWER or QHASNOTANSWER instead.`,
      );
    }
    const findQuestion = this.questionResponseVariables.find(
      (each) => each.questionVariable === questionVariables[0],
    );
    return findQuestion ? this.operatorOR(findQuestion.response) : 0;
  }

  private operatorEQUAL(variables: string[]) {
    if (!variables.length || variables.length !== 2) {
      throw new Error("EQUAL only take two variables");
    }
    const responseVariable = variables[0];
    const compareString = variables[1];
    if (!this.responseVariableHasInAnswer(responseVariable)) {
      return 0;
    }
    const foundAnswer = this.answers.find((eachAns) =>
      !!eachAns.ansResponse.find(
        (eachRes) =>
          !!eachRes[responseVariable] &&
          eachRes[responseVariable].toLowerCase() === compareString.toLowerCase(),
      ),
    );
    return foundAnswer ? 1 : 0;
  }

  private operatorNOTEQUAL(variables: string[]) {
    if (!variables.length || variables.length !== 2) {
      throw new Error("NOTEQUAL only take two variables");
    }
    const responseVariable = variables[0];
    const compareString = variables[1];
    if (!this.responseVariableHasInAnswer(responseVariable)) {
      return 0;
    }
    const foundAnswer = this.answers.find((eachAns) =>
      !!eachAns.ansResponse.find(
        (eachRes) =>
          !!eachRes[responseVariable] &&
          eachRes[responseVariable].toLowerCase() !== compareString.toLowerCase(),
      ),
    );
    return foundAnswer ? 1 : 0;
  }

  private parseComparableDate(value: string | null): number | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      const time = Date.UTC(Number(y), Number(m) - 1, Number(d));
      if (!isNaN(time)) {
        return time;
      }
    }

    const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
      const [, mm, dd, yyyy] = usMatch;
      const month = Number(mm);
      const day = Number(dd);
      const year = Number(yyyy);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const time = Date.UTC(year, month - 1, day);
        if (!isNaN(time)) {
          return time;
        }
      }
    }

    const parsed = Date.parse(trimmed);
    if (!isNaN(parsed)) {
      return parsed;
    }

    return null;
  }

  private operatorCOMPARE(variables: string[]) {
    if (!variables.length || (variables.length !== 3 && variables.length !== 4)) {
      throw new Error("COMPARE take three parameters, or four when a comparison type is given");
    }
    const responseVariable = variables[0];
    const operator = variables[1];
    const compareString = variables[2];
    const compareType = variables[3] ? variables[3].toUpperCase() : "NUMBER";

    const compareOperators = ["=", "!=", "<", ">", ">=", "<="];

    if (!compareOperators.includes(operator)) {
      throw new Error("COMPARE operator must be compare symbol " + compareOperators.join(", "));
    }

    if (compareType !== "NUMBER" && compareType !== "DATE") {
      throw new Error("COMPARE's comparison type must be NUMBER or DATE");
    }

    if (this.isAnswerNotExistsResponseVariable(responseVariable)) {
      throw new Error(
        `${responseVariable} can't be used here — use QHASNOTANSWER on the question variable instead.`,
      );
    }

    const ans = this.answers;

    function findAnswer() {
      let answerText: string | null = null;
      ans.forEach((eachAns) => {
        eachAns.ansResponse.forEach((eachRes) => {
          if (eachRes[responseVariable] && !answerText) {
            answerText = eachRes[responseVariable];
          }
        });
      });
      return answerText;
    }

    const _ans = findAnswer();

    if (operator === "=") {
      return this.operatorEQUAL([responseVariable, compareString]);
    } else if (operator === "!=") {
      return this.operatorNOTEQUAL([responseVariable, compareString]);
    }

    let _ansValue: number;
    let _compareValue: number;

    if (compareType === "DATE") {
      const ansDate = this.parseComparableDate(_ans);
      const compareDate = this.parseComparableDate(compareString);
      if (ansDate === null || compareDate === null) {
        return 0;
      }
      _ansValue = ansDate;
      _compareValue = compareDate;
    } else {
      _ansValue = Number(_ans);
      _compareValue = Number(compareString);
      if (!_ans || isNaN(_ansValue) || isNaN(_compareValue)) {
        return 0;
      }
    }

    if (operator === "<") {
      return _ansValue < _compareValue ? 1 : 0;
    } else if (operator === ">") {
      return _ansValue > _compareValue ? 1 : 0;
    } else if (operator === ">=") {
      return _ansValue >= _compareValue ? 1 : 0;
    } else if (operator === "<=") {
      return _ansValue <= _compareValue ? 1 : 0;
    }

    return 0;
  }

  private operatorQHASANSWER(questionVariables: string[]) {
    if (!questionVariables.length || questionVariables.length > 1) {
      throw new Error("QHASANSWER only take one question variable");
    }
    if (!this.isQuestionVariablePresentInQuestion(questionVariables[0])) {
      throw new Error(`Question variable not found: ${questionVariables[0]}`);
    }
    const findQuestion = this.questionResponseVariables.find(
      (each) => each.questionVariable === questionVariables[0],
    );
    if (!findQuestion) {
      throw new Error(`${questionVariables[0]} not exist`);
    }
    return this.questionHasRealAnswer(questionVariables[0], findQuestion) ? 1 : 0;
  }

  private operatorQHASNOTANSWER(questionVariables: string[]) {
    if (!questionVariables.length || questionVariables.length > 1) {
      throw new Error("QHASNOTANSWER only take one question variable");
    }
    if (!this.isQuestionVariablePresentInQuestion(questionVariables[0])) {
      throw new Error(`Question variable not found: ${questionVariables[0]}`);
    }
    const findQuestion = this.questionResponseVariables.find(
      (each) => each.questionVariable === questionVariables[0],
    );
    if (!findQuestion) {
      throw new Error(`${questionVariables[0]} not exist`);
    }
    return !this.questionHasRealAnswer(questionVariables[0], findQuestion) ? 1 : 0;
  }

  private operatorIMPORT(equation: string[]) {
    if (!equation.length || equation.length !== 1) {
      throw new Error("IMPORT only take one equation");
    }

    const currentEquation = equation[0];
    const findEquation = this.allEquations.find((each) => each.name === currentEquation);

    if (!findEquation) {
      throw new Error(currentEquation + " not found");
    }

    if (findEquation.result === null) {
      throw new Error(currentEquation + " has no result");
    }

    if (this.selfDisplayOrder != null && findEquation.display_order != null) {
      if (findEquation.display_order === this.selfDisplayOrder) {
        throw new Error(currentEquation + " is this same rank — a rank cannot IMPORT itself.");
      }
      if (findEquation.display_order < this.selfDisplayOrder) {
        throw new Error(currentEquation + " is a higher rank and cannot be imported into a lower rank.");
      }
    }

    return findEquation.result ? 1 : 0;
  }

  private calculateEachOperator(operatorName: string, argArr: string[]) {
    const _argArr = [...argArr.filter((each) => each !== "CONVERTED-OPERATOR")];
    const _operatorName = operatorName.toUpperCase();

    if (_operatorName === "AND") {
      return this.operatorAND(_argArr);
    } else if (_operatorName === "OR") {
      return this.operatorOR(_argArr);
    } else if (_operatorName === "QAND") {
      return this.operatorQAND(_argArr);
    } else if (_operatorName === "QOR") {
      return this.operatorQOR(_argArr);
    } else if (_operatorName === "EQUAL") {
      return this.operatorEQUAL(_argArr);
    } else if (_operatorName === "NOTEQUAL") {
      return this.operatorNOTEQUAL(_argArr);
    } else if (_operatorName === "COMPARE") {
      return this.operatorCOMPARE(_argArr);
    } else if (_operatorName === "QHASANSWER") {
      return this.operatorQHASANSWER(_argArr);
    } else if (_operatorName === "QHASNOTANSWER") {
      return this.operatorQHASNOTANSWER(_argArr);
    } else if (_operatorName === "IMPORT") {
      return this.operatorIMPORT(_argArr);
    }
    return 0;
  }

  private iterateArray(arr: ILogicalExpression) {
    arr.forEach((element, index) => {
      if (Array.isArray(element)) {
        if (!element.some(Array.isArray)) {
          const operatorName = arr[index - 1];
          const params = arr[index];
          arr[index - 1] = "CONVERTED-OPERATOR";
          arr[index] = this.calculateEachOperator(operatorName as string, params as string[]);
        } else {
          this.iterateArray(element);
        }
      }
    });
    return arr;
  }

  private calculate(arr: ILogicalExpression): string | number | ILogicalExpression {
    const newArr = this.iterateArray(arr);
    if (!newArr.every((each: any) => typeof each === "string")) {
      if (newArr[0] === "CONVERTED-OPERATOR") {
        return newArr[1];
      }
      return this.calculate(newArr);
    }
    return 0;
  }

  public getResult() {
    const hasEquationError = this.getHasError();
    if (hasEquationError) {
      throw new Error(`Equation has a syntax error at index ${hasEquationError}: ${this.equationRaw}`);
    }
    return this.calculate(this.equationStack) as number;
  }

  // Same recursive "does this look like a nested AND/OR sub-expression" test equationErrorRanges.ts's
  // `walk` already uses — only AND/OR's real engine (operatorAND/operatorOR above) accepts a
  // nested operator call as one of its own arguments.
  private allowsNested(operatorName: string) {
    return operatorName === "AND" || operatorName === "OR";
  }

  // The record's actual typed value stored under a given response variable's key, if any —
  // used to surface the real answer text for an open-ended question's "_answer_exists"
  // variable, which otherwise only shows as a plain 0/1 leaf like every categorical one.
  private findAnswerValue(responseVar: string): string | undefined {
    for (const eachAns of this.answers) {
      for (const eachRes of eachAns.ansResponse) {
        if (eachRes[responseVar]) {
          return String(eachRes[responseVar]);
        }
      }
    }
    return undefined;
  }

  private buildLeafNode(variable: string): IDryRunStepNode {
    try {
      const result = this.responseVariableHasInAnswer(variable) ? 1 : 0;
      const answerValue = variable.endsWith(ANSWER_EXISTS_SUFFIX) ? this.findAnswerValue(variable) : undefined;
      return { label: variable, kind: "leaf", result, answerValue };
    } catch (error: any) {
      return { label: variable, kind: "leaf", result: 0, detail: error?.message };
    }
  }

  private buildQuestionLeafNode(questionVar: string): IDryRunStepNode {
    try {
      if (!this.isQuestionVariablePresentInQuestion(questionVar)) {
        return {
          label: questionVar,
          kind: "leaf",
          result: 0,
          detail: `Question variable not found: ${questionVar}`,
        };
      }
      const findQuestion = this.questionResponseVariables.find(
        (each) => each.questionVariable === questionVar,
      )!;
      const result = this.questionHasRealAnswer(questionVar, findQuestion) ? 1 : 0;
      let answerValue: string | undefined;
      if (this.isOpenEndedQuestionVariable(questionVar)) {
        const answerExistsVar = findQuestion.response.find((each) => each.endsWith(ANSWER_EXISTS_SUFFIX));
        answerValue = answerExistsVar ? this.findAnswerValue(answerExistsVar) : undefined;
      }
      return { label: questionVar, kind: "leaf", result, answerValue };
    } catch (error: any) {
      return { label: questionVar, kind: "leaf", result: 0, detail: error?.message };
    }
  }

  private buildLiteralNode(value: string): IDryRunStepNode {
    return { label: value, kind: "literal", result: 0 };
  }

  // Every operator's arguments, broken into child rows — mirrors what that argument actually
  // means for THIS operator (a question variable for QAND/QOR/QHASANSWER/QHASNOTANSWER, a
  // response variable followed by comparison literals for EQUAL/NOTEQUAL/COMPARE) rather than
  // treating every argument as an evaluable variable.
  private buildArgumentChildren(operatorName: string, args: string[]): IDryRunStepNode[] {
    switch (operatorName) {
      case "QAND":
      case "QOR": {
        // QAND/QOR's real engine (operatorQAND/operatorQOR above) is AND/OR over ALL of the
        // question's OWN response variables — NOT "was this question answered" (that's what
        // QHASANSWER/QHASNOTANSWER check, via questionHasRealAnswer, a different condition
        // entirely). Showing a single "has an answer" leaf here would contradict the operator's
        // own 0/1 whenever a question was answered but not every option was selected — so this
        // decomposes into the SAME per-response-variable leaves AND/OR itself renders, making the
        // result directly explainable from its children.
        const questionVar = args[0];
        const findQuestion = this.questionResponseVariables.find(
          (each) => each.questionVariable === questionVar,
        );
        if (!findQuestion) {
          return questionVar != null ? [this.buildQuestionLeafNode(questionVar)] : [];
        }
        return findQuestion.response.map((responseVar) => this.buildLeafNode(responseVar));
      }
      case "QHASANSWER":
      case "QHASNOTANSWER":
        return args[0] != null ? [this.buildQuestionLeafNode(args[0])] : [];
      case "EQUAL":
      case "NOTEQUAL":
        return [args[0], args[1]]
          .filter((each) => each != null)
          .map((arg, index) => (index === 0 ? this.buildLeafNode(arg) : this.buildLiteralNode(arg)));
      case "COMPARE":
        return args
          .filter((each) => each != null)
          .map((arg, index) => (index === 0 ? this.buildLeafNode(arg) : this.buildLiteralNode(arg)));
      default:
        return [];
    }
  }

  // Builds a display tree for ONE rank's equation alongside computing its result, walking the
  // parser's [operatorName, argsArray] pair form (same shape equationErrorRanges.ts's `walk`
  // consumes) rather than getResult()'s mutate-in-place array — much easier to instrument for
  // a step-by-step UI. `buildImportedTree` is supplied by the caller (dryRunChainResolver.ts) so
  // this class doesn't need to know how to parse/resolve OTHER ranks' equations itself.
  public getResultWithSteps(
    buildImportedTree: (rankName: string) => IDryRunStepNode | null,
  ): { result: number; tree: IDryRunStepNode } {
    const hasEquationError = this.getHasError();
    if (hasEquationError) {
      throw new Error(`Equation has a syntax error at index ${hasEquationError}: ${this.equationRaw}`);
    }

    const exprPair = this.getEquationStack();
    const tree = this.evaluateNode(exprPair, buildImportedTree);
    return { result: tree.result, tree };
  }

  private evaluateNode(
    exprPair: ILogicalExpression,
    buildImportedTree: (rankName: string) => IDryRunStepNode | null,
  ): IDryRunStepNode {
    const operatorName = typeof exprPair[0] === "string" ? exprPair[0].toUpperCase() : "";
    const argsArray = exprPair[1];

    if (!Array.isArray(argsArray)) {
      return { label: operatorName, kind: "operator", result: 0, detail: "Malformed expression" };
    }

    const allowsNestedExpressions = this.allowsNested(operatorName);
    const children: IDryRunStepNode[] = [];
    const plainArgs: string[] = [];

    for (let i = 0; i < argsArray.length; i++) {
      const item = argsArray[i];
      const next = argsArray[i + 1];
      if (
        allowsNestedExpressions &&
        typeof item === "string" &&
        /^(AND|QAND|OR|QOR|IMPORT|EQUAL|NOTEQUAL|COMPARE|QHASANSWER|QHASNOTANSWER)$/.test(item.toUpperCase()) &&
        Array.isArray(next)
      ) {
        children.push(this.evaluateNode([item, next], buildImportedTree));
        i++;
        continue;
      }
      if (typeof item === "string") {
        plainArgs.push(item);
      }
    }

    try {
      if (operatorName === "AND" || operatorName === "OR") {
        plainArgs.forEach((variable) => children.push(this.buildLeafNode(variable)));
        const result =
          operatorName === "AND"
            ? this.operatorAND(children.map((c) => c.result))
            : this.operatorOR(children.map((c) => c.result));
        return { label: operatorName, kind: "operator", result, children };
      }

      if (operatorName === "IMPORT") {
        const rankName = plainArgs[0];
        const result = this.operatorIMPORT(plainArgs);
        return {
          label: `IMPORT(${rankName})`,
          kind: "import",
          result,
          importedRankName: rankName,
          importedEquationText: this.allEquations.find((each) => each.name === rankName)?.equation,
          importedTree: buildImportedTree(rankName),
        };
      }

      const result = this.calculateEachOperator(operatorName, plainArgs);
      return {
        label: operatorName,
        kind: "operator",
        result,
        children: this.buildArgumentChildren(operatorName, plainArgs),
      };
    } catch (error: any) {
      return { label: operatorName, kind: "operator", result: 0, detail: error?.message, children };
    }
  }
}

export default DryRunCalculateEquation;
